import { useEffect, useState } from "react";
import { RefreshCw, ShoppingCart, Loader2, Copy, Check } from "lucide-react";
import KrogerStorePicker, { getFavoriteStore, type StoreInfo } from "./KrogerStorePicker";
import { trackFeature } from "@/lib/analytics";
import { logActivity } from "@/lib/activityLog";
import { useSavedLocation, useZipPrefill } from "@/hooks/useSavedLocation";
import { useKroger } from "@/hooks/useKroger";
import { useAuth } from "@/hooks/useAuth";
import { saveKrogerStore } from "@/lib/krogerStore";
import { attachRowIds, deriveAddedRowIds } from "@/lib/krogerCartAdded";

/**
 * If a Kroger product name comes back mostly uppercase (>60% of letters),
 * convert to title case for display only. Never mutates the underlying data
 * sent to the cart API.
 */
function formatProductName(name?: string): string {
  if (!name) return name || "";
  const letters = name.replace(/[^a-zA-Z]/g, "");
  if (!letters.length) return name;
  const upperRatio = letters.replace(/[^A-Z]/g, "").length / letters.length;
  if (upperRatio > 0.6) {
    return name.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return name;
}

const KROGER_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/kroger-prices`;
const CART_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/kroger-cart`;
const ZIP_KEY = "kin_kroger_zip";

interface ProductResult {
  item: string;
  found: boolean;
  productName?: string;
  price?: string;
  size?: string;
  productId?: string;
}

interface KrogerResults {
  store: StoreInfo;
  results: ProductResult[];
  locationId: string;
}

/** Common grocery section/category headers that should never be saved as items. */
export const CATEGORY_HEADERS = new Set<string>([
  // Produce
  "produce", "fruits", "vegetables", "fruits and vegetables", "fruits & vegetables",
  // Meat & Seafood
  "meat", "meat and seafood", "meat & seafood", "seafood", "poultry", "deli",
  // Dairy & Eggs
  "dairy", "dairy and eggs", "dairy & eggs", "eggs",
  // Frozen
  "frozen", "frozen foods",
  // Pantry — CRITICAL: includes the multi-word variants
  "pantry", "pantry staples", "pantry and dry goods", "pantry & dry goods",
  "dry goods", "pantry/dry goods",
  // Snacks & Beverages
  "snacks", "snack foods", "beverages", "drinks",
  // Bakery
  "bakery", "bread", "breads",
  // Other catchall
  "other", "miscellaneous", "misc", "other items",
  // Specialty
  "canned goods", "canned", "condiments", "sauces", "grains", "pasta",
  "pasta and grains", "spices", "herbs", "herbs and spices", "baking", "baking goods",
  // Non-food (defensive — should NEVER appear with new prompt)
  "household", "personal care", "toiletries", "paper goods", "cleaning",
  "pet", "pet supplies", "baby", "baby supplies", "protein", "proteins",
]);

/** Non-food keyword safety net — items containing any of these get filtered out. */
const NON_FOOD_KEYWORDS = [
  "shampoo", "conditioner", "soap", "body wash", "bodywash",
  "toothpaste", "toothbrush", "deodorant", "lotion", "sunscreen",
  "razor", "razors", "shaving", "feminine", "tampon", "pad ", // " pad " trailing space avoids "pad thai"
  "detergent", "dish soap", "dishwasher", "sponge", "paper towel",
  "toilet paper", "tissue", "napkin", "trash bag", "ziploc", "plastic wrap", "foil",
  "diaper", "wipes", "formula", // baby supplies
  "litter", "pet food", "dog food", "cat food", "treats for",
  "vitamin", "supplement", "medication", "advil", "tylenol", "ibuprofen",
  "battery", "batteries", "light bulb", "candle",
];

export function containsNonFoodKeyword(item: string): boolean {
  const lower = item.toLowerCase();
  return NON_FOOD_KEYWORDS.some((kw) => lower.includes(kw));
}

export function isCategoryHeader(s: string): boolean {
  const norm = s.toLowerCase().replace(/[:.]+$/, "").trim();
  if (!norm) return true;
  if (CATEGORY_HEADERS.has(norm)) return true;
  // Single short word that isn't a real grocery item (e.g. "Other", "Misc")
  if (!/\s/.test(norm) && norm.length <= 5 && /^[a-z]+$/.test(norm)) {
    if (CATEGORY_HEADERS.has(norm)) return true;
  }
  return false;
}

/**
 * Hidden structural tags that the model emits for save/render orchestration.
 * If one of these leaks into a grocery section as its own line, exclude it.
 * Match is EXACT (after trim + lowercase) so legitimate items containing
 * brackets like "Flour [all-purpose]" are preserved.
 */
const STRUCTURAL_TAG_LINES = new Set<string>([
  "[meal_plan]",
  "[/meal_plan]",
  "[recipe]",
  "[/recipe]",
  "[grocery_list]",
  "[/grocery_list]",
  "[recipe_title]",
  "[/recipe_title]",
  "[todo_list]",
  "[/todo_list]",
  "[open_budget_tracker]",
  "[open_tool]",
]);

function isStructuralTag(s: string): boolean {
  return STRUCTURAL_TAG_LINES.has(s.trim().toLowerCase());
}

// Strict structured marker — opening + closing tags, dash-prefixed items, 3+ items.
const GROCERY_LIST_REGEX = /\[GROCERY_LIST\]\s*\n([\s\S]*?)\n?\[\/GROCERY_LIST\]/i;
// Legacy fallback — only honored when the surrounding content actually looks
// like food (meal plan / recipe). Prevents false positives on day-planning
// responses that happen to mention "grocery list" in passing.
const OLD_GROCERY_REGEX = /🛒\s*GROCERY LIST:\s*\n((?:[\s\-*•\d.][^\n]*\n?){3,})/i;
const MEAL_CONTENT_RE = /\b(breakfast|lunch|dinner|meal plan|recipe|ingredients|serves|cook|bake|preheat|tbsp|tsp)\b/i;

function extractItemsFromBlock(block: string): string[] {
  const lines = block.split("\n");
  const items: string[] = [];
  for (const line of lines) {
    const cleaned = line.replace(/^[\s\-\*•\d.]+/, "").trim();
    if (!cleaned) continue;
    if (/^\*\*[^*]+\*\*:?$/.test(cleaned)) continue;     // **Header** or **Header:**
    if (/^\*\*[^*]+:\*\*$/.test(cleaned)) continue;      // **Header:** with colon inside bold
    if (cleaned.length < 2 || cleaned.length > 80) continue;
    const final = cleaned.replace(/\*\*/g, "").replace(/:$/, "").trim();
    if (!final) continue;
    if (isCategoryHeader(final)) continue;
    if (isStructuralTag(final)) continue;
    if (containsNonFoodKeyword(final)) continue;
    items.push(final);
  }
  return items;
}

/**
 * Recognized grocery section headers for the markdown-fallback path.
 * Lowercased, punctuation stripped. Matched against bare lines and
 * **bold** lines alike. Requires at least 2 distinct headers in the
 * response to qualify, to prevent false positives on short bullet lists
 * or non-grocery content that happens to mention "snacks" once.
 */
const FALLBACK_SECTION_HEADERS = new Set<string>([
  "produce",
  "dairy",
  "dairy and eggs",
  "dairy & eggs",
  "meat",
  "meat and seafood",
  "meat & seafood",
  "protein",
  "proteins",
  "pantry",
  "pantry and dry goods",
  "pantry & dry goods",
  "dry goods",
  "frozen",
  "frozen foods",
  "snacks",
  "beverages",
  "drinks",
  "bakery",
  "bread",
  "deli",
  "canned goods",
  "other",
]);

function normalizeHeaderLine(line: string): string {
  return line
    .replace(/\*\*/g, "")
    .replace(/^#+\s*/, "")
    .replace(/[:.]+$/, "")
    .trim()
    .toLowerCase();
}

/**
 * Fallback parser. Scans for >=2 recognized grocery section headers and,
 * if found, harvests the dash/star bulleted lines underneath them.
 * Only runs when the structured [GROCERY_LIST] tag is absent.
 */
function parseGroceryItemsFallback(content: string): string[] {
  const lines = content.split("\n");
  const headerIndices: number[] = [];
  for (let i = 0; i < lines.length; i++) {
    const norm = normalizeHeaderLine(lines[i]);
    if (norm && FALLBACK_SECTION_HEADERS.has(norm)) {
      headerIndices.push(i);
    }
  }
  if (headerIndices.length < 2) return [];

  const items: string[] = [];
  const seen = new Set<string>();
  for (const idx of headerIndices) {
    for (let j = idx + 1; j < lines.length; j++) {
      const raw = lines[j];
      const trimmed = raw.trim();
      if (!trimmed) break; // blank line ends the section
      // Stop if we hit another header (recognized or markdown heading)
      const nextHeader = normalizeHeaderLine(raw);
      if (j !== idx && FALLBACK_SECTION_HEADERS.has(nextHeader)) break;
      if (/^#+\s/.test(trimmed)) break;

      // Only accept bullet lines
      if (!/^[-*]\s+/.test(trimmed)) continue;
      const cleaned = trimmed
        .replace(/^[-*]\s+/, "")
        .replace(/\*\*/g, "")
        .replace(/:$/, "")
        .trim();
      if (!cleaned) continue;
      if (cleaned.length < 2 || cleaned.length > 80) continue;
      if (isCategoryHeader(cleaned)) continue;
      if (isStructuralTag(cleaned)) continue;
      if (containsNonFoodKeyword(cleaned)) continue;
      const key = cleaned.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      items.push(cleaned);
    }
  }
  return items.length >= 3 ? items : [];
}

/** Parse grocery list items from the message content (structured block, with legacy fallback). */
export function parseGroceryItems(content: string): string[] {
  const structured = content.match(GROCERY_LIST_REGEX);
  if (structured) {
    const items = extractItemsFromBlock(structured[1]);
    if (items.length >= 3) return items;
  }
  // Legacy: only when the response is genuinely meal/recipe content.
  if (MEAL_CONTENT_RE.test(content)) {
    const old = content.match(OLD_GROCERY_REGEX);
    if (old) {
      const items = extractItemsFromBlock(old[1]);
      if (items.length >= 3) return items;
    }
  }
  // Markdown-section fallback: catches responses where the model
  // emitted a grouped list without the [GROCERY_LIST] wrapper.
  return parseGroceryItemsFallback(content);
}

export function hasGroceryList(content: string): boolean {
  return parseGroceryItems(content).length >= 3;
}

/**
 * One item handed to this panel by a caller that HAS rows behind it (the My Kin
 * grocery list). Carrying the row id is what lets the cart result be matched
 * back to a specific `grocery_list.id` instead of to its text — see #942 and
 * krogerCartAdded.ts.
 */
export interface KrogerDirectItem {
  id: string;
  item: string;
  /** #1602 — a pinned staple's exact product. The cart adds this, not a name match. */
  krogerProductId?: string | null;
}

interface KrogerPricingProps {
  messageContent?: string;
  /**
   * Items to price. A bare string is for callers with no row behind the text —
   * the chat panel parses items out of a message and has nothing to tick off.
   * Those callers pass no `onCartSuccess`, so the missing id never reaches a
   * write; it is honest about what it does not know rather than inventing one.
   */
  directItems?: Array<string | KrogerDirectItem>;
  /** When true (My Kin grocery list), cap the product list height with internal scroll. */
  compact?: boolean;
  /** Fired after results render so the chat can re-pin its scroll anchor. */
  onLayoutComplete?: () => void;
  /**
   * Fired after items are successfully added to Kroger cart, with the
   * `grocery_list` ROW IDS that actually landed there — not everything she
   * sent, and not everything on her list. The caller uses this to tick off
   * exactly those rows; anything Kroger couldn't find, anything she unticked
   * here, and anything that failed mid-add must stay unchecked.
   *
   * ⚠️ Row ids, not item text (#942). Two rows can hold the same text, and
   * ticking by text struck off both when one of them went into the cart.
   */
  onCartSuccess?: (addedRowIds: string[]) => void;
}

export default function KrogerPricing({ messageContent, directItems, compact = false, onLayoutComplete, onCartSuccess }: KrogerPricingProps) {
  // #740 — token custody moved server-side; see useKroger.tsx.
  const kroger = useKroger();
  const [step, setStep] = useState<"idle" | "zip" | "loading" | "results" | "error">("idle");
  const [zipInput, setZipInput] = useState("");
  const [currentZip, setCurrentZip] = useState(localStorage.getItem(ZIP_KEY) || "");
  const [results, setResults] = useState<KrogerResults | null>(null);
  const [error, setError] = useState("");
  const [swappingIdx, setSwappingIdx] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [cartStatus, setCartStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [cartErrors, setCartErrors] = useState<string[]>([]);
  const [cartError, setCartError] = useState<string | null>(null);
  const [checkedSet, setCheckedSet] = useState<Set<number>>(new Set());

  // Prefill the zip box from her saved zip (#533) — she still taps the button.
  // Deliberately does NOT auto-submit: the Kroger flow's five layers of
  // defence all sit downstream of that tap, and this is a text-field prefill,
  // nothing more. localStorage stays the Kroger-specific store of record, so a
  // different store zip she's already used here still wins.
  const { zip: savedZip } = useSavedLocation();
  useZipPrefill(step === "zip", savedZip, zipInput, setZipInput);

  // When results arrive, default-check every found row.
  useEffect(() => {
    if (step === "results" && results) {
      const next = new Set<number>();
      results.results.forEach((r, idx) => {
        if (r.found && r.productId) next.add(idx);
      });
      setCheckedSet(next);
    }
  }, [step, results]);

  const toggleChecked = (idx: number) => {
    setCheckedSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const checkedCount = checkedSet.size;

  // Normalised to one shape so everything below reads the same regardless of
  // which caller supplied the items. `id` is undefined for text-only callers.
  const sourceItems: Array<{ id?: string; item: string; krogerProductId?: string | null }> = (
    directItems || (messageContent ? parseGroceryItems(messageContent) : [])
  ).map((d) => (typeof d === "string" ? { item: d } : d));

  // What goes over the wire to kroger-prices — unchanged, still bare strings.
  const items = sourceItems.map((i) => i.item);
  // #1602 — aligned with `items`. Null for every line that isn't a pinned
  // staple, and kroger-prices searches those by name exactly as before.
  const productIds = sourceItems.map((i) => i.krogerProductId ?? null);

  // Notify parent (e.g. ChatMessages) once results have rendered so it can
  // re-pin its scroll anchor below the now-fully-laid-out panel.
  useEffect(() => {
    if (step === "results" && results && onLayoutComplete) {
      const id = requestAnimationFrame(() => onLayoutComplete());
      return () => cancelAnimationFrame(id);
    }
  }, [step, results, onLayoutComplete]);

  // #1603 — the store she just priced at is the one the weekly stock check
  // uses for her pinned staples. Saved to her account once per store change,
  // not on every open; a failed save is logged and never blocks pricing.
  const { user: authUser } = useAuth();
  const [savedStoreId, setSavedStoreId] = useState<string | null>(null);
  const rememberStore = (store: { id?: string; name?: string } | undefined) => {
    const id = store?.id;
    if (!authUser?.id || !id || id === savedStoreId) return;
    // Marked saved only once the write is confirmed, so a failed save is
    // retried on the next pricing run instead of never (Copilot on #1621).
    void saveKrogerStore(authUser.id, store).then((res) => {
      if (res.ok) setSavedStoreId(id);
    });
  };

  const fetchPricesForStore = async (store: StoreInfo) => {
    setStep("loading");
    try {
      const resp = await fetch(KROGER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          action: "search",
          locationId: store.id,
          storeName: store.name,
          items,
          productIds,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.error || "Something went wrong");
        setStep("error");
        return;
      }
      setResults(data);
      setStep("results");
      rememberStore(data?.store);
    } catch {
      setError("Couldn't connect to Kroger right now. Try again in a sec!");
      setStep("error");
    }
  };

  const fetchPricesByZip = async (zip: string) => {
    setStep("loading");
    try {
      const resp = await fetch(KROGER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ action: "search", zipCode: zip, items, productIds }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.error || "Something went wrong");
        setStep("error");
        return;
      }
      setResults(data);
      setStep("results");
      rememberStore(data?.store);
    } catch {
      setError("Couldn't connect to Kroger right now. Try again in a sec!");
      setStep("error");
    }
  };

  const handleFindPrices = () => {
    // Check for favorite store first
    const fav = getFavoriteStore();
    if (fav) {
      fetchPricesForStore(fav);
      return;
    }
    const saved = localStorage.getItem(ZIP_KEY);
    if (saved) {
      setCurrentZip(saved);
      fetchPricesByZip(saved);
    } else {
      setStep("zip");
    }
  };

  const handleZipSubmit = () => {
    const zip = zipInput.trim();
    if (!/^\d{5}$/.test(zip)) return;
    localStorage.setItem(ZIP_KEY, zip);
    setCurrentZip(zip);
    fetchPricesByZip(zip);
  };

  const handleStoreChange = (store: StoreInfo) => {
    fetchPricesForStore(store);
  };

  const handleZipChange = (newZip: string) => {
    localStorage.setItem(ZIP_KEY, newZip);
    setCurrentZip(newZip);
    fetchPricesByZip(newZip);
  };

  const handleSwap = async (idx: number) => {
    if (!results) return;
    const product = results.results[idx];
    setSwappingIdx(idx);
    try {
      const resp = await fetch(KROGER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          action: "swap",
          itemTerm: product.item,
          locationId: results.locationId,
          skipProductId: product.productId,
        }),
      });
      const data = await resp.json();
      if (data.result?.found) {
        setResults((prev) => {
          if (!prev) return prev;
          const updated = [...prev.results];
          updated[idx] = data.result;
          return { ...prev, results: updated };
        });
      }
    } catch {
      // silently fail swap
    }
    setSwappingIdx(null);
  };

  const estimatedTotal = results?.results
    .filter((r) => r.found && r.price && r.price !== "Price N/A")
    .reduce((sum, r) => sum + parseFloat(r.price!.replace("$", "")), 0);

  const handleCopyList = () => {
    if (!results) return;
    const lines = results.results.map((r) =>
      r.found
        ? `- ${r.productName}${r.size ? ` (${r.size})` : ""} - ${r.price}`
        : `- ${r.item} - Not found`
    );
    const text = `Kroger - ${results.store.name}\n\n${lines.join("\n")}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const CART_FAILURE_COPY =
    "Hmm, couldn't connect to Kroger right now. Your list is still here — try again in a sec, or copy the list and add to Kroger manually.";

  const handleAddToCart = async () => {
    if (!results) return;
    setCartError(null);

    try {
      const accessToken = await kroger.getValidAccessToken();
      if (!accessToken) { await kroger.connect(); return; }

      setCartStatus("loading");
      setCartErrors([]);
      const foundIdx = results.results
        .map((_, idx) => idx)
        .filter((idx) => {
          const r = results.results[idx];
          return r.found && r.productId && checkedSet.has(idx);
        });
      const foundItems = foundIdx.map((idx) => results.results[idx]);

      const resp = await fetch(CART_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ action: "addToCart", accessToken, items: foundItems }),
      });
      if (!resp.ok) throw new Error("cart-failed");
      const data = await resp.json();
      if (data.failed?.length > 0) setCartErrors(data.failed.map((f: any) => f.productName));
      if (data.added > 0) {
        trackFeature("kroger_cart_add", { items_added: data.added, items_failed: data.failed?.length ?? 0 });
        logActivity("kroger_send", { items_added: data.added, items_failed: data.failed?.length ?? 0 });
        setCartStatus("success");
        setTimeout(() => setCartStatus("idle"), 3000);
        // Only the rows that actually reached the cart — see krogerCartAdded.ts
        // for why this is narrower than both her list and what we sent.
        //
        // Row identity rides the INDEX across the round trip, guarded by a text
        // re-check so a future divergence fails closed — see attachRowIds.
        if (onCartSuccess) {
          const sentWithRows = attachRowIds(sourceItems, results.results, foundIdx);
          onCartSuccess(deriveAddedRowIds(sentWithRows, data.failed));
        }
      } else {
        setCartStatus("error");
        if (!data.failed?.length) setCartErrors(["No items could be added. Please try again."]);
      }
    } catch (err) {
      console.error("[Kroger cart] connection failed:", err);
      setCartStatus("idle");
      setCartErrors([]);
      setCartError(CART_FAILURE_COPY);
    }
  };

  // === RENDER ===

  if (step === "idle") {
    return (
      <button
        onClick={handleFindPrices}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-body transition-colors whitespace-nowrap flex-shrink-0"
        style={{ backgroundColor: "#5050C8", color: "#FFFFFF", fontSize: "clamp(12px, 2.8vw, 13px)" }}
      >
        <ShoppingCart size={13} />
        Load Kroger prices
      </button>
    );
  }

  if (step === "zip") {
    return (
      <div className="mt-2 p-3 rounded-2xl bg-kin-ghost border border-kin-soft-violet w-full max-w-full box-border">
        <p className="text-sm font-body text-foreground mb-2">
          What's your zip code? I'll find your nearest Kroger and check prices 🛒
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="numeric"
            maxLength={5}
            value={zipInput}
            onChange={(e) => setZipInput(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && handleZipSubmit()}
            placeholder="e.g. 30301"
            className="flex-1 px-3 py-2 rounded-xl text-sm font-body bg-card border border-kin-soft-violet focus:outline-none focus:ring-2 focus:ring-kin-periwinkle"
            autoFocus
          />
          <button
            onClick={handleZipSubmit}
            disabled={!/^\d{5}$/.test(zipInput.trim())}
            className="px-4 py-2 rounded-xl text-sm font-body text-primary-foreground disabled:opacity-50 transition-colors"
            style={{ backgroundColor: "#5050C8" }}
          >
            Go
          </button>
        </div>
      </div>
    );
  }

  if (step === "loading") {
    return (
      <div className="mt-2 p-4 rounded-2xl bg-kin-ghost border border-kin-soft-violet flex items-center gap-2 w-full max-w-full box-border">
        <Loader2 size={16} className="animate-spin" style={{ color: "#5050C8" }} />
        <span className="text-sm font-body text-foreground">Checking Kroger prices...</span>
      </div>
    );
  }

  if (step === "error") {
    return (
      <div className="mt-2 p-3 rounded-2xl bg-kin-ghost border border-kin-soft-violet w-full max-w-full box-border">
        <p className="text-sm font-body text-foreground">{error}</p>
        <button
          onClick={() => setStep("idle")}
          className="mt-2 text-xs font-body underline"
          style={{ color: "#5050C8" }}
        >
          Try again
        </button>
      </div>
    );
  }

  // Results
  return (
    <div className="mt-2 rounded-2xl bg-kin-ghost border border-kin-soft-violet w-full max-w-full box-border" style={{ overflow: "visible" }}>
      {/* Header — relative + z-index lifts the whole header (and its dropdown child) above the product list below */}
      <div
        className="flex items-center w-full box-border rounded-t-2xl"
        style={{ backgroundColor: "#5050C8", padding: "10px 12px", gap: "6px", position: "relative", zIndex: 20 }}
      >
        <ShoppingCart size={14} className="text-primary-foreground flex-shrink-0" />
        {results?.store && (
          <div className="flex-1 min-w-0">
            <KrogerStorePicker
              currentStore={results.store}
              zipCode={currentZip}
              onStoreChange={handleStoreChange}
              onZipChange={handleZipChange}
            />
          </div>
        )}
        <button
          onClick={handleCopyList}
          className="flex items-center gap-1 rounded-full font-body font-semibold text-primary-foreground bg-white/20 hover:bg-white/30 transition-colors flex-shrink-0 whitespace-nowrap"
          style={{ fontSize: "clamp(12px, 2.8vw, 13px)", padding: "4px 8px" }}
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? "Copied! ✓" : "Copy list"}
        </button>
      </div>

      {/* Product list — capped height + internal scroll when compact (My Kin grocery list) */}
      {/* paddingBottom: 1 keeps the last row's divider from painting flush with the scroll edge */}
      <div
        className="divide-y divide-kin-soft-violet relative"
        style={
          compact
            ? {
                paddingTop: 8,
                paddingBottom: 1,
                maxHeight: 360,
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
              }
            : { paddingTop: 8, paddingBottom: 1 }
        }
      >
        {results?.results.map((product, idx) => {
          const isChecked = checkedSet.has(idx);
          const rowOpacity = product.found && !isChecked ? 0.5 : 1;
          return (
            <label
              key={idx}
              htmlFor={product.found ? `kroger-row-${idx}` : undefined}
              className="flex items-start w-full box-border cursor-pointer"
              style={{
                padding: "10px 12px",
                gap: "10px",
                minHeight: 44,
                opacity: rowOpacity,
                transition: "opacity 0.15s ease",
              }}
            >
              {product.found ? (
                <input
                  id={`kroger-row-${idx}`}
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleChecked(idx)}
                  className="flex-shrink-0 cursor-pointer"
                  style={{
                    width: 20,
                    height: 20,
                    marginTop: 2,
                    accentColor: "#5050C8",
                    borderColor: "#C8C8F4",
                  }}
                />
              ) : (
                // Spacer to keep not-found rows aligned with checkbox column
                <span style={{ width: 20, flexShrink: 0 }} aria-hidden />
              )}
              <div className="flex-1 min-w-0">
                {product.found ? (
                  <>
                    <p className="text-sm font-body text-foreground font-medium break-words" style={{ overflowWrap: "break-word", wordBreak: "break-word" }}>
                      {formatProductName(product.productName)}
                    </p>
                    <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginTop: "2px", flexWrap: "wrap" }}>
                      {product.size && (
                        <span style={{ fontSize: "12px", color: "#54525C" }}>{product.size}</span>
                      )}
                      {product.price && (
                        <span style={{ fontSize: "14px", fontWeight: 700, color: "#5050C8" }}>
                          {product.price}
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-body text-foreground">{product.item}</p>
                    <p className="text-xs font-body italic" style={{ color: "#54525C" }}>
                      Not found at this store
                    </p>
                  </>
                )}
              </div>
              {product.found && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSwap(idx);
                  }}
                  disabled={swappingIdx === idx}
                  className="flex items-center gap-1 px-3 py-2 rounded-full text-xs font-body transition-colors shrink-0 whitespace-nowrap border"
                  style={{
                    backgroundColor: swappingIdx === idx ? "#C8C8F4" : "#FFFFFF",
                    color: "#5050C8",
                    borderColor: "#5050C8",
                    minHeight: 36,
                  }}
                >
                  {swappingIdx === idx ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <RefreshCw size={14} />
                  )}
                  Swap
                </button>
              )}
            </label>
          );
        })}

      </div>

      {/* Estimated total */}
      {estimatedTotal != null && estimatedTotal > 0 && (
        <div
          className="px-3 py-2.5 flex justify-between items-center border-t"
          style={{ borderColor: "#C8C8F4" }}
        >
          <span className="text-sm font-body font-semibold text-foreground">Estimated Total</span>
          <span className="text-sm font-body font-bold" style={{ color: "#5050C8" }}>
            ${estimatedTotal.toFixed(2)}
          </span>
        </div>
      )}

      {/* Add to Kroger Cart button */}
      <div className="px-3 py-3 border-t" style={{ borderColor: "#C8C8F4" }}>
        {cartError ? (
          <div
            style={{
              padding: 14,
              backgroundColor: "#FFF4F4",
              borderRadius: 12,
              border: "1px solid #F4D8D8",
            }}
          >
            <p
              className="font-body"
              style={{ color: "#54525C", fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}
            >
              {cartError}
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setCartError(null)}
                className="flex-1 py-2 rounded-xl text-sm font-body font-semibold text-primary-foreground transition-colors"
                style={{ backgroundColor: "#5050C8" }}
              >
                Try again
              </button>
              <button
                onClick={handleCopyList}
                className="flex-1 py-2 rounded-xl text-sm font-body font-semibold transition-colors border"
                style={{ backgroundColor: "#FFFFFF", color: "#5050C8", borderColor: "#5050C8" }}
              >
                {copied ? "Copied! ✓" : "Copy list"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <button
              onClick={handleAddToCart}
              disabled={cartStatus === "loading" || cartStatus === "success" || checkedCount === 0}
              className="w-full py-2.5 rounded-xl text-sm font-body font-semibold text-primary-foreground transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: cartStatus === "success" ? "#22C55E" : "#5050C8" }}
            >
              {cartStatus === "loading" ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={15} className="animate-spin" />
                  Adding to cart...
                </span>
              ) : cartStatus === "success" ? (
                "✓ Added to your Kroger cart!"
              ) : checkedCount === 0 ? (
                "Select items to add"
              ) : checkedCount === 1 ? (
                "🛒 Add 1 item to Kroger cart"
              ) : (
                `🛒 Add ${checkedCount} items to Kroger cart`
              )}
            </button>
            {cartStatus === "error" && cartErrors.length > 0 && (
              <div className="mt-2 text-xs font-body" style={{ color: "#DC2626" }}>
                {cartErrors.length === 1 && !cartErrors[0].includes("items") ? (
                  <p>Couldn't add: {cartErrors[0]}</p>
                ) : (
                  <>
                    <p>Some items couldn't be added:</p>
                    <ul className="list-disc list-inside mt-1">
                      {cartErrors.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
